import React, { useEffect, useMemo, useRef, useState } from 'react';
import StepBasicDetails from './StepBasicDetails';
import StepOccasion from './StepOccasion';
import StepCake from './StepCake';
import StepAddons from './StepAddons';
import StepTerms from './StepTerms';
import { GetApiHandler, PostApiHandler, apiBaseURL } from '../utils/Api';

const STORAGE_KEY = 'bookingDraft';
const RAZORPAY_CHECKOUT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

const loadRazorpayCheckout = () => {
  if (window.Razorpay) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${RAZORPAY_CHECKOUT_URL}"]`);
    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = RAZORPAY_CHECKOUT_URL;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Unable to load Razorpay checkout.'));
    document.body.appendChild(script);
  });
};
let cachedOccasions = null;
let cachedProducts = null;
let cachedVariations = null;
let cachedCategories = null;
let cachedExtras = null;
let cachedGifts = null;
let cachedServices = null;
let productFetchPromise = null;
let occasionFetchPromise = null;

const BookingStepOne = ({ onClose, theatre, selectedSlot, showDate, bookingFee = 0, advancePayableAmount = 0 }) => {
  const [step, setStep] = useState(1);
  const [peopleCount, setPeopleCount] = useState(2);
  const [occasion, setOccasion] = useState('');
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [selectedCake, setSelectedCake] = useState('');
  const [selectedCakeId, setSelectedCakeId] = useState(null);
  const [customSize, setCustomSize] = useState('');
  const [selectedVariation, setSelectedVariation] = useState(null);
  const variationsMapRef = useRef(new Map());
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [bookingName, setBookingName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [nickname, setNickname] = useState('');
  const [selectedOccasionId, setSelectedOccasionId] = useState(null);
  const [customOccasionText, setCustomOccasionText] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [decorationSelected, setDecorationSelected] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCouponCode, setAppliedCouponCode] = useState('');
  const [couponDiscountAmount, setCouponDiscountAmount] = useState(0);
  const [couponApplying, setCouponApplying] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [submitError, setSubmitError] = useState('');
  const RUPEE = '\u20b9';
  const rupee = (value) => `${RUPEE}${value}`;
  // hydrate from localStorage
  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return;
      const data = JSON.parse(saved);
      setPeopleCount(data.peopleCount ?? data.adults ?? 2);
      setOccasion(data.occasion ?? '');
      setActiveCategoryId(data.activeCategoryId ?? null);
      setSelectedCake(data.selectedCake ?? '');
      setSelectedCakeId(data.selectedCakeId ?? null);
      setCustomSize(data.customSize ?? '');
      setSelectedAddons(data.selectedAddons ?? []);
      setBookingName(data.bookingName ?? '');
      setPhone(data.phone ?? '');
      setEmail(data.email ?? '');
      setNickname(data.nickname ?? '');
      setAcceptedTerms(data.acceptedTerms ?? false);
      setSelectedOccasionId(data.selectedOccasionId ?? null);
      setCustomOccasionText(data.customOccasionText ?? '');
      setDecorationSelected(data.decorationSelected ?? false);
      setCouponCode(data.couponCode ?? '');
      setAppliedCouponCode(data.appliedCouponCode ?? '');
      setCouponDiscountAmount(data.couponDiscountAmount ?? 0);
    } catch (err) {
      console.warn('Failed to load draft booking', err);
    }
  }, []);

  // persist to localStorage
  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    const data = {
      peopleCount,
      occasion,
      activeCategoryId,
      selectedCake,
      selectedCakeId,
      customSize,
      selectedAddons,
      bookingName,
      phone,
      email,
      nickname,
      acceptedTerms,
      selectedOccasionId,
      customOccasionText,
      decorationSelected,
      couponCode,
      appliedCouponCode,
      couponDiscountAmount,
      theatre: theatre ? { id: theatre.id, name: theatre.name } : null,
      slot: selectedSlot ? { id: selectedSlot.id, label: selectedSlot.label } : null,
      showDate,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [
    peopleCount,
    occasion,
    activeCategoryId,
    selectedCake,
    selectedCakeId,
    customSize,
    selectedAddons,
    bookingName,
    phone,
    email,
    nickname,
    acceptedTerms,
    selectedOccasionId,
    customOccasionText,
    decorationSelected,
    couponCode,
    appliedCouponCode,
    couponDiscountAmount,
    theatre,
    selectedSlot,
    showDate,
  ]);

  const maxCapacity = Number(theatre?.raw?.max_capacity ?? theatre?.max_capacity ?? 10);
  const adjust = (delta, min = 1) => {
    setPeopleCount((curr) => {
      const next = Math.max(min, curr + delta);
      return next <= maxCapacity ? next : curr;
    });
  };

  const [occasions, setOccasions] = useState([]);
  const assetBase = apiBaseURL.replace(/\/api\/v1$/, '');
  const normalizeImagePath = (imagePath) => {
    const value = String(imagePath ?? '').trim();

    if (!value || value === 'null' || value === 'undefined') {
      return '';
    }

    return value.startsWith('http') ? value : `${assetBase}/${value}`;
  };
  const toMoney = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return '';
    return rupee(num.toLocaleString('en-IN', { maximumFractionDigits: 0 }));
  };
  const [categories, setCategories] = useState([]);
  const [cakes, setCakes] = useState([]);
  const [extras, setExtras] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [services, setServices] = useState([]);
  const [customOptions, setCustomOptions] = useState([]);

  useEffect(() => {
    const fetchOccasions = async () => {
      try {
        if (cachedOccasions) {
          setOccasions(cachedOccasions);
          return;
        }
        if (!occasionFetchPromise) {
          occasionFetchPromise = GetApiHandler({}, 'occasions');
        }
        const res = await occasionFetchPromise;
        const list = Array.isArray(res?.data) ? res.data : [];
        const mapped = list.map((item, idx) => ({
          id: item.id || idx,
          label: item.name || `Occasion ${idx + 1}`,
          img: normalizeImagePath(item.image_path),
        }));
        cachedOccasions = mapped;
        setOccasions(mapped);
      } catch (error) {
        console.warn('Occasions fetch failed', error);
      }
    };

    const fetchProductDataOnce = async () => {
      if (cachedProducts && cachedVariations && cachedCategories) {
        setCategories(cachedCategories);
        setCakes(cachedProducts);
        setExtras(cachedExtras || []);
        setGifts(cachedGifts || []);
        setServices(cachedServices || []);
        variationsMapRef.current = cachedVariations;
        if (activeCategoryId === null && cachedCategories.length) {
          setActiveCategoryId(cachedCategories[0].id);
        }
        return;
      }
      if (!productFetchPromise) {
        productFetchPromise = Promise.all([
          GetApiHandler({}, 'product-categories'),
          GetApiHandler({}, 'products'),
          GetApiHandler({}, 'product-variations'),
        ]);
      }
      try {
        const [catRes, productRes, variationRes] = await productFetchPromise;

        const catList = Array.isArray(catRes?.data) ? catRes.data : [];
        const filteredCats = catList.filter((c) => c.id === 1 || c.id === 2);
        cachedCategories = filteredCats;
        setCategories(filteredCats);
        if (activeCategoryId === null && filteredCats.length) {
          setActiveCategoryId(filteredCats[0].id);
        }

        const products = Array.isArray(productRes?.data) ? productRes.data : [];
        const variations = Array.isArray(variationRes?.data) ? variationRes.data : [];
        const byProduct = variations.reduce((acc, v) => {
          const list = acc.get(v.product_id) || [];
          list.push(v);
          acc.set(v.product_id, list);
          return acc;
        }, new Map());
        variationsMapRef.current = byProduct;
        cachedVariations = byProduct;

        if (products.length) {
          const mapped = products.map((item, idx) => {
            const firstVar = (byProduct.get(item.id) || [])[0];
            const priceValue = firstVar?.price ?? firstVar?.amount ?? firstVar?.price_amount;
            return {
              id: item.id || idx,
              category_id: item.category_id,
              label: item.name || `Cake ${idx + 1}`,
              img: normalizeImagePath(item.image_path),
              price: toMoney(priceValue) || '',
            };
          });
          cachedProducts = mapped;
          setCakes(mapped);
          const mapByCat = (catId) =>
            mapped
              .filter((p) => p.category_id === catId)
              .map((p) => ({
                id: p.id,
                label: p.label,
                price: p.price || '',
                img: p.img,
                product_variation_id: (byProduct.get(p.id)?.[0]?.id) || p.id,
              }));
          cachedExtras = mapByCat(3);
          cachedGifts = mapByCat(4);
          cachedServices = mapByCat(5);
          setExtras(cachedExtras);
          setGifts(cachedGifts);
          setServices(cachedServices);
        } else {
          cachedProducts = [];
          cachedExtras = [];
          cachedGifts = [];
          cachedServices = [];
          setCakes([]);
          setExtras([]);
          setGifts([]);
          setServices([]);
        }
      } catch (error) {
        console.warn('Cakes fetch failed', error);
      }
    };

    fetchOccasions();
    fetchProductDataOnce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isStepOne = step === 1;
  const isStepTwo = step === 2;
  const isStepThree = step === 3;
  const isStepFour = step === 4;
  const isStepFive = step === 5;

  const goNext = () => setStep((s) => Math.min(5, s + 1));
  const toggleAddon = (label) => {
    setSelectedAddons((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  const buildAddonsPayload = () =>
    selectedAddons
      .map((label) => {
        const product =
          extras.find((i) => i.label === label) ||
          gifts.find((i) => i.label === label) ||
          services.find((i) => i.label === label);

        if (!product) return null;

        return {
          product_id: product.id,
          product_variation_id: product.product_variation_id || product.id,
          quantity: 1,
        };
      })
      .filter(Boolean);

  // Reset selections when the slot or theatre changes to avoid stale payload values
  useEffect(() => {
    setSelectedCake('');
    setSelectedCakeId(null);
    setSelectedVariation(null);
    setCustomSize('');
    setCustomOptions([]);
    setSelectedAddons([]);
    setActiveCategoryId((cachedCategories && cachedCategories[0]?.id) || null);
    setDecorationSelected(false);
    setCouponCode('');
    setAppliedCouponCode('');
    setCouponDiscountAmount(0);
    setCouponError('');
    setCouponSuccess('');
    setSuccessMessage('');
    setSubmitError('');
  }, [theatre?.id, selectedSlot?.id]);

  const handleSelectCake = (cake) => {
    setSelectedCake(cake.label);
    setSelectedCakeId(cake.id);
    setCustomSize('');
    setSelectedVariation(null);

    const variations = variationsMapRef.current.get(cake.id) || [];
    if (variations.length) {
      const mapped = variations.map((v, i) => {
        const attrs = v.attributes || {};
        const label =
          attrs.size || attrs.bundle || attrs.label || attrs.serves
            ? Object.entries(attrs)
                .map(([k, val]) => `${k}: ${val}`)
                .join(', ')
            : v.sku || `Variant ${i + 1}`;
        return {
          label,
          price: toMoney(v.price ?? v.amount ?? v.price_amount) || '',
          raw: v,
        };
      });
      setCustomOptions(mapped);
      setSelectedVariation(mapped[0] || null);
      setCustomSize(mapped[0]?.label || '');
    } else {
      setCustomOptions([]);
      setSelectedVariation(null);
      setCustomSize('');
    }
  };

  const priceLookup = useMemo(() => {
    const map = new Map();
    cakes.forEach((item) => map.set(item.label, item.price));
    extras.forEach((item) => map.set(item.label, item.price));
    gifts.forEach((item) => map.set(item.label, item.price));
    services.forEach((item) => map.set(item.label, item.price));
    if (selectedCake && selectedVariation?.price) {
      map.set(selectedCake, selectedVariation.price);
      map.set(selectedVariation.label, selectedVariation.price);
    }
    return map;
  }, [cakes, extras, gifts, services, selectedCake, selectedVariation]);

  const priceToNumber = (value) => {
    if (!value) return 0;
    const cleaned = String(value).replace(/[^\d.]/g, '');
    const num = Number(cleaned);
    return Number.isNaN(num) ? 0 : num;
  };

  useEffect(() => {
    if (!couponCode.trim()) {
      setAppliedCouponCode('');
      setCouponDiscountAmount(0);
      setCouponError('');
      setCouponSuccess('');
      return;
    }

    if (appliedCouponCode && couponCode.trim().toUpperCase() !== appliedCouponCode) {
      setAppliedCouponCode('');
      setCouponDiscountAmount(0);
      setCouponSuccess('');
    }
  }, [couponCode, appliedCouponCode]);

  useEffect(() => {
    if (!appliedCouponCode || !selectedSlot || !theatre) {
      return;
    }

    applyCoupon(appliedCouponCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peopleCount, decorationSelected, selectedCakeId, selectedVariation, selectedAddons, theatre?.id, selectedSlot?.id]);

  const includedPeople = Number(theatre?.raw?.base_number_of_people ?? theatre?.base_number_of_people ?? 0);
  const perPersonCost = Number(theatre?.raw?.per_person_cost ?? theatre?.per_person_cost ?? 0);
  const decorationPrice = Number(theatre?.raw?.decoration_price ?? theatre?.decoration_price ?? 750);
  const decorationAvailable = theatre?.raw?.decoration_available ?? theatre?.decoration_available ?? true;
  const isPhoneValid = String(phone || '').replace(/\D/g, '').length === 10;
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());
  const extraPeopleCount = Math.max(peopleCount - includedPeople, 0);
  const extraPeopleAmount = extraPeopleCount * perPersonCost;
  const decorationAmount = decorationSelected && decorationAvailable ? decorationPrice : 0;
  const addonTotal = selectedAddons.reduce((sum, label) => sum + priceToNumber(priceLookup.get(label)), 0);
  const cakeTotal = selectedCake ? priceToNumber(priceLookup.get(selectedCake)) : 0;
  const variationTotal = selectedVariation ? priceToNumber(selectedVariation.price) : 0;
  const theatreTotal = bookingFee || 0;
  const subtotalBeforeDiscount = theatreTotal + extraPeopleAmount + decorationAmount + cakeTotal + variationTotal + addonTotal;
  const totalAmount = Math.max(subtotalBeforeDiscount - couponDiscountAmount, 0);
  const advancePayable = Math.min(advancePayableAmount || 0, totalAmount);
  const balanceAmount = Math.max(totalAmount - advancePayable, 0);
  const trimmedCouponCode = couponCode.trim().toUpperCase();

  const startRazorpayPayment = async (bookingInfo, customerName) => {
    await loadRazorpayCheckout();

    const orderResponse = await PostApiHandler(
      { booking_id: bookingInfo.booking_id },
      'payments/razorpay/order'
    );

    if (!orderResponse?.data?.razorpay_order_id || !orderResponse?.data?.key_id) {
      throw new Error(orderResponse?.message || 'Unable to start Razorpay payment.');
    }

    const order = orderResponse.data;

    return new Promise((resolve, reject) => {
      const checkout = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency || 'INR',
        name: 'Cineflix Theater',
        description: `Booking #${bookingInfo.booking_id} advance payment`,
        order_id: order.razorpay_order_id,
        prefill: {
          name: customerName,
          email,
          contact: phone,
        },
        notes: {
          booking_id: String(bookingInfo.booking_id),
        },
        theme: {
          color: '#111827',
        },
        handler: async (paymentResponse) => {
          try {
            const verifyResponse = await PostApiHandler(
              {
                booking_id: bookingInfo.booking_id,
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature,
              },
              'payments/razorpay/verify'
            );

            if (verifyResponse?.message !== 'Payment verified.') {
              throw new Error(verifyResponse?.message || 'Payment verification failed.');
            }

            resolve(verifyResponse);
          } catch (error) {
            reject(error);
          }
        },
        modal: {
          ondismiss: () => reject(new Error('Payment was cancelled. Your booking is still pending.')),
        },
      });

      checkout.on('payment.failed', (response) => {
        reject(new Error(response?.error?.description || 'Payment failed. Please try again.'));
      });

      checkout.open();
    });
  };

  const applyCoupon = async (overrideCode) => {
    const codeToApply = (overrideCode ?? couponCode).trim().toUpperCase();

    if (!codeToApply || !selectedSlot || !theatre) {
      return;
    }

    setCouponApplying(true);
    setCouponError('');

    try {
      const cakeObj = cakes.find((c) => c.id === selectedCakeId);
      const isCakeCategory = cakeObj && (cakeObj.category_id === 1 || cakeObj.category_id === 2);
      const variationsForCake = cakeObj ? variationsMapRef.current.get(cakeObj.id) || [] : [];
      const matchedVariation =
        selectedVariation?.raw?.id && variationsForCake.find((v) => v.id === selectedVariation.raw.id);
      const hasCake = Boolean(isCakeCategory && matchedVariation);

      const response = await PostApiHandler(
        {
          theater_id: theatre.id,
          show_id: selectedSlot.raw?.id || selectedSlot.id,
          adults_count: peopleCount,
          kids_3_10_count: 0,
          kids_below_3_count: 0,
          decoration_selected: decorationSelected,
          cake_product_id: hasCake ? selectedCakeId : null,
          cake_variation_id: hasCake ? matchedVariation.id : null,
          addons: buildAddonsPayload(),
          coupon_code: codeToApply,
        },
        'coupons/validate'
      );

      if (!response?.data) {
        throw new Error(
          response?.errors?.coupon_code?.[0] ||
            response?.message ||
            'Unable to apply coupon. Please check the code and try again.'
        );
      }

      const payload = response?.data;
      const nextDiscount = Number(payload?.discount_amount) || 0;

      setCouponCode(codeToApply);
      setAppliedCouponCode(payload?.coupon_code || codeToApply);
      setCouponDiscountAmount(nextDiscount);
      setCouponSuccess(
        nextDiscount > 0
          ? `Coupon applied. Discount ${rupee(nextDiscount)}.`
          : 'Coupon applied.'
      );
    } catch (error) {
      const message =
        error?.message ||
        'Unable to apply coupon. Please check the code and try again.';

      setAppliedCouponCode('');
      setCouponDiscountAmount(0);
      setCouponSuccess('');
      setCouponError(message);
    } finally {
      setCouponApplying(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedSlot || !theatre) return;
    if (selectedCake && customOptions.length > 0 && !selectedVariation) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const splitName = (name) => {
        if (!name) return { first: '', last: '' };
        const parts = name.trim().split(' ');
        return { first: parts[0] || '', last: parts.slice(1).join(' ') || parts[0] || '' };
      };
      const { first, last } = splitName(bookingName);

      const addonsPayload = buildAddonsPayload();

      const cakeObj = cakes.find((c) => c.id === selectedCakeId);
      const isCakeCategory = cakeObj && (cakeObj.category_id === 1 || cakeObj.category_id === 2);
      const variationsForCake = cakeObj ? variationsMapRef.current.get(cakeObj.id) || [] : [];
      const matchedVariation =
        selectedVariation?.raw?.id && variationsForCake.find((v) => v.id === selectedVariation.raw.id);
      const hasCake = Boolean(isCakeCategory && matchedVariation);
      const payload = {
        theater_id: theatre.id,
        show_id: selectedSlot.raw?.id || selectedSlot.id,
        occasion_id: selectedOccasionId,
        occasion_custom_text: selectedOccasionId ? null : customOccasionText.trim() || null,
        customer_first_name: first,
        customer_last_name: last,
        customer_email: email,
        customer_phone: phone,
        adults_count: peopleCount,
        kids_3_10_count: 0,
        kids_below_3_count: 0,
        decoration_selected: decorationSelected,
        cake_product_id: hasCake ? selectedCakeId : null,
        cake_variation_id: hasCake ? matchedVariation.id : null,
        addons: addonsPayload.length ? addonsPayload : null,
        coupon_code: appliedCouponCode || trimmedCouponCode || '',
      };
      const res = await PostApiHandler(payload, 'bookings');
      if (!res?.data) {
        throw new Error(
          res?.errors?.coupon_code?.[0] ||
            res?.message ||
            'Booking failed. Please review the details and try again.'
        );
      }
      const info = res?.data;
      const adv = info?.advance_payable_amount ?? bookingFee ?? 0;

      setSuccessMessage(
        info
          ? `Booking created (ID ${info.booking_id}). Opening Razorpay for ${rupee(adv)} advance payment.`
          : 'Booking created. Opening Razorpay payment.'
      );

      await startRazorpayPayment(info, bookingName);

      setSuccessMessage(
        info
          ? `Payment successful. Booking confirmed (ID ${info.booking_id}).`
          : 'Payment successful. Booking confirmed.'
      );
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(STORAGE_KEY);
      }
      setSubmitting(false);
      onClose();
    } catch (error) {
      console.error('Booking failed', error);
      setSubmitError(error?.message || 'Booking failed. Please review the details and try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="booking-overlay">
      <div className="booking-sheet">
        <div className="booking-header">
          <button
            className="back-link"
            type="button"
            onClick={step === 1 ? onClose : () => setStep((s) => Math.max(1, s - 1))}
          >
            <i className="bi bi-arrow-left" />
            Back
          </button>
          <div className="stepper stepper-lined">
            {['Basic Details', 'Occasion', 'Cake', 'Addons', 'Payment'].map((label, idx) => {
              const position = idx + 1;
              const state = position < step ? 'done' : position === step ? 'active' : '';
              return (
                <div key={label} className={`step ${state}`}>
                  <span className="step-dot" />
                  <span className="step-label">{label}</span>
                </div>
              );
            })}
          </div>
          <span className="stepper-note">Note: Pay only {rupee(advancePayable)} advance to reserve</span>
        </div>

        <div className="booking-body">
          {successMessage && <div className="booking-card" style={{ color: '#16a34a', fontWeight: 700 }}>{successMessage}</div>}
          {submitError && <div className="booking-card" style={{ color: '#dc2626', fontWeight: 700 }}>{submitError}</div>}
          <div className="booking-main">
            {isStepOne && (
              <StepBasicDetails
                peopleCount={peopleCount}
                adjust={adjust}
                bookingName={bookingName}
                setBookingName={setBookingName}
                phone={phone}
                setPhone={setPhone}
                email={email}
                setEmail={setEmail}
                theatreName={theatre?.name}
                cityName={theatre?.location}
                slotLabel={selectedSlot?.label}
                showDate={showDate}
                maxCapacity={maxCapacity}
                decorationSelected={decorationSelected}
                setDecorationSelected={setDecorationSelected}
                decorationPrice={decorationPrice}
                decorationAvailable={decorationAvailable}
                couponCode={couponCode}
                setCouponCode={setCouponCode}
                onApplyCoupon={() => applyCoupon()}
                couponApplying={couponApplying}
                couponCanApply={Boolean(trimmedCouponCode)}
                couponError={couponError}
                couponSuccess={couponSuccess}
              />
            )}

            {isStepTwo && (
              <StepOccasion
                occasions={occasions}
                occasion={occasion}
                setOccasion={setOccasion}
                customOccasionText={customOccasionText}
                setCustomOccasionText={setCustomOccasionText}
                nickname={nickname}
                setNickname={setNickname}
                onSelectOccasion={(item) => setSelectedOccasionId(item.id === 'other' ? null : item.id)}
              />
            )}

            {isStepThree && (
              <StepCake
                categories={categories}
                activeCategoryId={activeCategoryId}
                setActiveCategoryId={setActiveCategoryId}
                cakes={cakes}
                selectedCakeId={selectedCakeId}
                selectedCake={selectedCake}
                onSelectCake={handleSelectCake}
                customSize={customSize}
                setCustomSize={setCustomSize}
                customOptions={customOptions}
                onSelectVariation={(opt) => {
                  setSelectedVariation(opt);
                  setCustomSize(opt?.label || '');
                }}
              />
            )}

            {isStepFour && (
              <StepAddons
                extras={extras}
                gifts={gifts}
                services={services}
                selectedAddons={selectedAddons}
                toggleAddon={toggleAddon}
              />
            )}

            {isStepFive && (
              <StepTerms
                accepted={acceptedTerms}
                setAccepted={setAcceptedTerms}
                onSubmit={handleSubmit}
                submitting={submitting}
                bookingFee={advancePayable}
              />
            )}
          </div>

          <aside className="booking-summary">
            <div className="summary-card">
              <div className="summary-total">
                <span>Total</span>
                <strong>{rupee(totalAmount)}</strong>
              </div>
              <div className="summary-row summary-toggle">Booking summary</div>
              <div className="summary-list">
                <div className="summary-row">
                  <span>
                    <i className="bi bi-ticket-perforated" /> Theater
                  </span>
                  <span>{rupee(theatreTotal)}</span>
                </div>
                {extraPeopleCount > 0 && perPersonCost > 0 && (
                  <div className="summary-row">
                    <span>
                      Extra people ({extraPeopleCount} x {rupee(perPersonCost)})
                    </span>
                    <span>{rupee(extraPeopleAmount)}</span>
                  </div>
                )}
                {decorationAmount > 0 && (
                  <div className="summary-row">
                    <span>Decoration</span>
                    <span>{rupee(decorationAmount)}</span>
                  </div>
                )}
                {selectedCake && (
                  <div className="summary-row">
                    <span>{selectedCake}</span>
                    <span>{priceLookup.get(selectedCake)}</span>
                  </div>
                )}
                {selectedVariation && (
                  <div className="summary-row">
                    <span>{selectedVariation.label}</span>
                    <span>{selectedVariation.price}</span>
                  </div>
                )}
                {selectedAddons.map((addon) => (
                  <div key={addon} className="summary-row">
                    <span>{addon}</span>
                    <span>{priceLookup.get(addon)}</span>
                  </div>
                ))}
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>{rupee(subtotalBeforeDiscount)}</span>
                </div>
                {couponDiscountAmount > 0 && (
                  <div className="summary-row">
                    <span>Coupon{appliedCouponCode ? ` (${appliedCouponCode})` : ''}</span>
                    <span>-{rupee(couponDiscountAmount)}</span>
                  </div>
                )}
                <div className="summary-row">
                  <span>Total amount</span>
                  <span>{rupee(totalAmount)}</span>
                </div>
                <div className="summary-row highlight">
                  <span>Advance payable</span>
                  <span>{rupee(advancePayable)}</span>
                </div>
                <div className="summary-row">
                  <div>
                    <div>Balance amount</div>
                    <small className="note">(Payable at the venue)</small>
                  </div>
                  <span>{rupee(balanceAmount)}</span>
                </div>
              </div>
            </div>
          </aside>
        </div>

        <div className="booking-footer">
          {!isStepFive && (
            <button
              className="btn-next-step"
              type="button"
              onClick={goNext}
              disabled={
                (isStepOne && (!bookingName || !phone || !email || !isPhoneValid || !isEmailValid)) ||
                (isStepTwo &&
                  (!occasion ||
                    !nickname ||
                    (occasion === 'Others' && !customOccasionText.trim()))) ||
                (isStepThree && selectedCake && customOptions.length > 0 && !selectedVariation)
              }
            >
              Next step
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingStepOne;
